
/** The sidecar written beside the samples by `tools/build_terrain_normals.mjs`. */
import { clamp } from "../clamp";
import type { ObjectSurface } from "./objectsurface";

export interface HeightFieldMeta {
  readonly width: number;
  readonly height: number;
  readonly minM: number;
  readonly maxM: number;
}

/** Metres per grid step. The bake is world-aligned over the whole map, so this
 *  is the map's own extent divided by the sample count rather than anything the
 *  file has to carry. */
export interface HeightFieldExtent {
  readonly worldWidthMm: number;
  readonly worldHeightMm: number;
}

export class HeightField {
  readonly present: boolean;

  private readonly samples: Uint16Array;
  private readonly width: number;
  private readonly rows: number;
  private readonly minMm: number;
  private readonly spanMm: number;
  private readonly worldWidthMm: number;
  private readonly worldHeightMm: number;

  private constructor(
    samples: Uint16Array,
    meta: HeightFieldMeta,
    extent: HeightFieldExtent,
    present: boolean,
  ) {
    this.samples = samples;
    this.width = Math.max(1, meta.width);
    this.rows = Math.max(1, meta.height);
    this.minMm = meta.minM * 1000;
    this.spanMm = (meta.maxM - meta.minM) * 1000;
    this.worldWidthMm = extent.worldWidthMm;
    this.worldHeightMm = extent.worldHeightMm;
    this.present = present;
  }

  static flat(extent: HeightFieldExtent): HeightField {
    return new HeightField(
      new Uint16Array(1),
      { width: 1, height: 1, minM: 0, maxM: 0 },
      extent,
      false,
    );
  }

  static from(
    samples: Uint16Array,
    meta: HeightFieldMeta,
    extent: HeightFieldExtent,
  ): HeightField {
    // A file whose sample count does not match its own header is a truncated
    // download, and reading past the end of it would silently return zeros --
    // which looks like a cliff at whatever point the download stopped.
    if (samples.length < meta.width * meta.height || meta.width < 1 || meta.height < 1) {
      return HeightField.flat(extent);
    }
    return new HeightField(samples, meta, extent, true);
  }

  private objects: ObjectSurface | null = null;

  /** The placed objects whose own surfaces count as ground; null for none. */
  setObjectSurface(objects: ObjectSurface | null): void {
    this.objects = objects;
  }

  private buildings: ObjectSurface | null = null;

  /** The standing guild buildings whose own surfaces count as ground; null for none. */
  setBuildingSurface(buildings: ObjectSurface | null): void {
    this.buildings = buildings;
  }

  /** Ground height in millimetres at a world position: the higher of the
   *  terrain and any object surface over it, which is `CMapOutdoor::GetHeight`
   *  (`MapOutdoor.cpp:746-773`). */
  at(xMm: number, yMm: number): number {
    const terrain = this.terrainAt(xMm, yMm);
    const object = this.objects?.heightAt(xMm, yMm) ?? null;
    const building = this.buildings?.heightAt(xMm, yMm) ?? null;
    return Math.max(terrain, object ?? terrain, building ?? terrain);
  }

  /** The terrain alone, bilinear and clamped: `GetTerrainHeight`. */
  terrainAt(xMm: number, yMm: number): number {
    if (!this.present) return 0;

    // Sample centres sit on the grid corners, so the last sample is at the far
    // edge rather than one step short of it -- hence the divide by (n - 1).
    const u = (xMm / this.worldWidthMm) * (this.width - 1);
    const v = (yMm / this.worldHeightMm) * (this.rows - 1);

    const x0 = clamp(Math.floor(u), 0, this.width - 1);
    const y0 = clamp(Math.floor(v), 0, this.rows - 1);
    const x1 = clamp(x0 + 1, 0, this.width - 1);
    const y1 = clamp(y0 + 1, 0, this.rows - 1);
    const fx = clamp(u - x0, 0, 1);
    const fy = clamp(v - y0, 0, 1);

    const h00 = this.samples[y0 * this.width + x0];
    const h10 = this.samples[y0 * this.width + x1];
    const h01 = this.samples[y1 * this.width + x0];
    const h11 = this.samples[y1 * this.width + x1];

    const top = h00 + (h10 - h00) * fx;
    const bottom = h01 + (h11 - h01) * fx;
    const raw = top + (bottom - top) * fy;

    return this.minMm + (raw / 65535) * this.spanMm;
  }

  /** The raw bytes and shape, for uploading the identical data to the GPU. */
  get texture(): {
    samples: Uint16Array;
    width: number;
    height: number;
    minMm: number;
    spanMm: number;
  } {
    return {
      samples: this.samples,
      width: this.width,
      height: this.rows,
      minMm: this.minMm,
      spanMm: this.spanMm,
    };
  }
}

/** Walk the ground this often when asking whether a hill sits on a view ray. */
export const LOS_STEP_MM = 1_000;

export function lineOfSightClear(
  field: Pick<HeightField, "present" | "at">,
  fromXMm: number,
  fromYMm: number,
  fromZMm: number,
  toXMm: number,
  toYMm: number,
  toZMm: number,
): boolean {
  if (!field.present) return true;
  const dx = toXMm - fromXMm;
  const dy = toYMm - fromYMm;
  const dz = toZMm - fromZMm;
  const horiz = Math.hypot(dx, dy);
  if (!(horiz > LOS_STEP_MM)) return true;
  const steps = Math.max(2, Math.ceil(horiz / LOS_STEP_MM));
  for (let i = 1; i < steps; i++) {
    const t = i / steps;
    const ground = field.at(fromXMm + dx * t, fromYMm + dy * t);
    const rayZ = fromZMm + dz * t;
    // Inclusive: a ray that skims the lip is still behind the nearer ground
    // in the GL depth test (`PythonTextTail.cpp:342`). A 250 mm grace let
    // plaza-lip wolves keep a bar with no mesh.
    if (ground >= rayZ) return false;
  }
  return true;
}
